import React, { useEffect, useState, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '';

const ROLE_LABEL = { THERAPIST: 'Therapist', OFFICE_ADMIN: 'Office Admin', PORTAL_ADMIN: 'Portal Admin' };

function formatUptime(seconds) {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const parts = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  parts.push(`${m}m`);
  return parts.join(' ');
}

function formatTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const card = {
  background: '#fff',
  borderRadius: 10,
  boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
  padding: '20px 24px',
  marginBottom: 24,
};

const sectionTitle = {
  fontSize: 15,
  fontWeight: 700,
  color: 'var(--primary-color)',
  margin: '0 0 16px',
  paddingBottom: 10,
  borderBottom: '2px solid #EEF0F6',
};

const statBox = {
  background: 'var(--secondary-color)',
  borderRadius: 8,
  padding: '14px 18px',
  textAlign: 'center',
  flex: 1,
  minWidth: 110,
};

const pill = (color) => ({
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: 12,
  fontSize: 11,
  fontWeight: 600,
  background: color + '20',
  color: color,
});

const ROLE_COLOR = {
  THERAPIST: '#2A9D8F',
  OFFICE_ADMIN: 'var(--primary-color)',
  PORTAL_ADMIN: '#8338EC',
};

// Backup verdict colors. There is no --warning-color token yet (see
// frontend-design notes), so amber is spelled out here.
const BACKUP_LEVEL = {
  ok:      { label: 'OK',      color: 'var(--success-color)' },
  warning: { label: 'Warning', color: '#B7791F' },
  failed:  { label: 'FAILED',  color: 'var(--error-color)' },
};

function formatBytes(n) {
  if (!Number.isFinite(n)) return '—';
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function ServerInfoPage() {
  const [data, setData] = useState(null);
  const [backups, setBackups] = useState(null);      // null = not loaded, false = unavailable
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshed, setRefreshed] = useState(null);

  const load = useCallback(async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const res = await fetch(`${API}/api/admin/server-stats`, { headers });
      if (!res.ok) throw new Error('Failed to load');
      const json = await res.json();
      setData(json);
      setRefreshed(new Date());
      setError(null);
    } catch (e) {
      setError('Could not load server data.');
    } finally {
      setLoading(false);
    }
    // Backup status is a separate call so a problem there never hides the rest.
    try {
      const res = await fetch(`${API}/api/admin/backup-status`, { headers });
      setBackups(res.ok ? await res.json() : false);
    } catch (e) {
      setBackups(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 16px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--primary-color)' }}>Server &amp; Portal Info</h1>
            {refreshed && (
              <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--muted-color)' }}>
                Last refreshed {formatTime(refreshed)}
              </p>
            )}
          </div>
          <button
            onClick={load}
            style={{ background: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
          >
            ↻ Refresh
          </button>
        </div>

        {loading && <p style={{ color: 'var(--muted-color)' }}>Loading...</p>}
        {error && <p style={{ color: 'var(--error-color)' }}>{error}</p>}

        {data && (
          <>
            {/* Portal Stats */}
            <div style={card}>
              <h2 style={sectionTitle}>Portal Overview</h2>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {[
                  { label: 'Active Users', value: data.portal.active_users },
                  { label: 'Therapists', value: data.portal.therapists },
                  { label: 'Admins', value: data.portal.admins },
                  { label: 'Total Clients', value: data.portal.total_clients },
                  { label: 'Total Notes', value: Number(data.portal.total_notes).toLocaleString() },
                  { label: 'Total Logins', value: Number(data.portal.total_logins).toLocaleString() },
                ].map(s => (
                  <div key={s.label} style={statBox}>
                    <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--primary-color)' }}>{s.value}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted-color)', marginTop: 4 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Server Status */}
            <div style={card}>
              <h2 style={sectionTitle}>Server Status</h2>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {[
                  { label: 'Node Version', value: data.server.nodeVersion },
                  { label: 'Platform', value: data.server.platform },
                  { label: 'Uptime', value: formatUptime(data.server.uptimeSeconds) },
                  { label: 'RSS Memory', value: `${data.server.memoryMB.rss} MB` },
                  { label: 'Heap Used', value: `${data.server.memoryMB.heapUsed} MB` },
                  { label: 'Heap Total', value: `${data.server.memoryMB.heapTotal} MB` },
                ].map(s => (
                  <div key={s.label} style={statBox}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--primary-color)' }}>{s.value}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted-color)', marginTop: 4 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Backups — status files from the nightly job (ops/backup/) */}
            <div style={card}>
              <h2 style={sectionTitle}>
                Backups
                {backups && BACKUP_LEVEL[backups.level] && (
                  <span style={{ ...pill(BACKUP_LEVEL[backups.level].color), marginLeft: 10, verticalAlign: 'middle' }}>
                    {BACKUP_LEVEL[backups.level].label}
                  </span>
                )}
              </h2>
              {backups === null && <p style={{ color: 'var(--muted-color)', margin: 0 }}>Loading...</p>}
              {backups === false && <p style={{ color: 'var(--muted-color)', margin: 0 }}>Backup status unavailable.</p>}
              {backups && (
                <>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {[
                      { label: 'Last Backup', value: timeAgo(backups.lastBackupAt), sub: formatTime(backups.lastBackupAt) },
                      { label: 'Backup Size', value: formatBytes(backups.backup?.sizeBytes), sub: backups.backup?.durationSeconds != null ? `${backups.backup.durationSeconds}s` : '' },
                      { label: 'Restore Test', value: backups.restoreTest ? (backups.restoreTest.ok ? 'Passed' : 'Failed') : 'Not yet', sub: backups.lastRestoreTestAt ? timeAgo(backups.lastRestoreTestAt) : '' },
                      { label: 'Tables Verified', value: backups.restoreTest ? backups.restoreTest.tablesChecked : '—', sub: backups.restoreTest?.mismatches?.length ? `${backups.restoreTest.mismatches.length} mismatch` : '' },
                      { label: 'Kept On Server', value: backups.backup?.keepDays ? `${backups.backup.keepDays} days` : '—', sub: 'plus droplet snapshots' },
                    ].map(s => (
                      <div key={s.label} style={statBox}>
                        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--primary-color)' }}>{s.value}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted-color)', marginTop: 4 }}>{s.label}</div>
                        {s.sub ? <div style={{ fontSize: 10, color: 'var(--muted-color)', marginTop: 2 }}>{s.sub}</div> : null}
                      </div>
                    ))}
                  </div>
                  {backups.problems?.length > 0 && (
                    <ul style={{ margin: '14px 0 0', paddingLeft: 20, color: 'var(--error-color)', fontSize: 13 }}>
                      {backups.problems.map(p => <li key={p}>{p}</li>)}
                    </ul>
                  )}
                  <p style={{ margin: '14px 0 0', fontSize: 12, color: 'var(--muted-color)' }}>
                    Runs nightly at 3:15 AM Eastern; the restore test runs Sundays. An email goes out at 8 AM Eastern if either is missing, failed, or overdue.
                  </p>
                </>
              )}
            </div>

            {/* Login Activity */}
            <div style={card}>
              <h2 style={sectionTitle}>Recent Login Activity <span style={{ fontWeight: 400, color: 'var(--muted-color)', fontSize: 12 }}>(last 100)</span></h2>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--secondary-color)' }}>
                      {['User', 'Role', 'IP Address', 'Time', 'Ago'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--primary-color)', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.recentLogins.map((row, i) => (
                      <tr key={row.id} style={{ background: i % 2 === 0 ? '#fff' : '#FAFAF8', borderBottom: '1px solid #F0EDE8' }}>
                        <td style={{ padding: '8px 12px', fontWeight: 500 }}>{row.first_name} {row.last_name}</td>
                        <td style={{ padding: '8px 12px' }}>
                          <span style={pill(ROLE_COLOR[row.role] || 'var(--text-strong)')}>{ROLE_LABEL[row.role] || row.role}</span>
                        </td>
                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: 'var(--text-strong)' }}>{row.ip_address}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--text-strong)', whiteSpace: 'nowrap' }}>{formatTime(row.logged_in_at)}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--muted-color)', whiteSpace: 'nowrap' }}>{timeAgo(row.logged_in_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Logins per day */}
            <div style={card}>
              <h2 style={sectionTitle}>Logins — Last 30 Days</h2>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {data.loginsByDay.map(row => (
                  <div key={row.day} style={{ textAlign: 'center', minWidth: 48 }}>
                    <div style={{
                      height: Math.max(4, Math.min(60, row.logins * 6)),
                      background: 'var(--primary-color)',
                      borderRadius: '3px 3px 0 0',
                      margin: '0 auto',
                      width: 28,
                    }} />
                    <div style={{ fontSize: 10, color: 'var(--muted-color)', marginTop: 3 }}>
                      {new Date(row.day + 'T12:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--primary-color)' }}>{row.logins}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Database Tables */}
            <div style={card}>
              <h2 style={sectionTitle}>Database Tables</h2>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--secondary-color)' }}>
                      {['Table', 'Rows', 'Size'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--primary-color)', fontWeight: 600 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.tables.map((row, i) => (
                      <tr key={row.table_name} style={{ background: i % 2 === 0 ? '#fff' : '#FAFAF8', borderBottom: '1px solid #F0EDE8' }}>
                        <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: 'var(--text-strong)' }}>{row.table_name}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--text-strong)' }}>{Number(row.row_count).toLocaleString()}</td>
                        <td style={{ padding: '8px 12px', color: 'var(--text-strong)' }}>{row.total_size}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
  );
}
